/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from 'helpers/ajax'
import { consoleLog } from 'utils/utility'
import { getEnglishCountryNameFromAnyCountryValue } from 'utils/hooks/useGetCountryCode'

type RequestActionSession = {
    access_token: string
}

type RequestActionState = {
    sessionState: {
        session: RequestActionSession
    }
}

type RequestActionDataWithFile = {
    file: File
} & Record<string, unknown>

const isRequestActionDataWithFile = (
    value: unknown
): value is RequestActionDataWithFile => {
    if (typeof value !== 'object' || value === null) {
        return false
    }

    if (!('file' in value)) {
        return false
    }

    const fileValue = (value as { file: unknown }).file
    return fileValue instanceof File
}

const COUNTRY_KEY_REGEX = /(^country$|_country$)/i

const shouldNormalizeCountryKey = (key: string): boolean => {
    return COUNTRY_KEY_REGEX.test(key)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (typeof value !== 'object' || value === null) {
        return false
    }

    if (value instanceof Date || value instanceof File || value instanceof Blob || value instanceof FormData) {
        return false
    }

    return !Array.isArray(value)
}

const normalizeCountriesInPayload = async <T>(payload: T): Promise<T> => {
    if (payload === null || typeof payload === 'undefined') {
        return payload
    }

    if (typeof payload === 'string' || typeof payload === 'number' || typeof payload === 'boolean') {
        return payload
    }

    if (Array.isArray(payload)) {
        const normalizedItems = await Promise.all(payload.map((item) => normalizeCountriesInPayload(item)))
        return normalizedItems as T
    }

    if (!isPlainObject(payload)) {
        return payload
    }

    const entries = await Promise.all(
        Object.entries(payload).map(async ([key, value]) => {
            if (typeof value === 'string' && shouldNormalizeCountryKey(key)) {
                const trimmedValue = value.trim()
                if (!trimmedValue) {
                    return [key, value]
                }

                const englishCountry = await getEnglishCountryNameFromAnyCountryValue(trimmedValue, trimmedValue)
                return [key, englishCountry]
            }

            return [key, await normalizeCountriesInPayload(value)]
        })
    )

    return Object.fromEntries(entries) as T
}

export default class RequestAction {
    static async makeAuthenticatedRequest<TResponse, TData = any>(
        getState: () => RequestActionState,
        url: string,
        data?: TData,
        method: 'get' | 'post' | 'delete' = 'post',
        responseType: 'json' | 'blob' = 'json'
    ): Promise<TResponse> {
        const normalizedData = await normalizeCountriesInPayload(data)

        const config: any = {
            
            responseType: responseType,
        }

        try {
            let response: TResponse
            if (method === 'delete') {
                config.data = normalizedData
                consoleLog(`RequestAction: DELETE ${url}`, normalizedData)
                response = (await ajax.delete(url, config)) as TResponse
            } else if (method === 'get') {
                config.params = normalizedData
                consoleLog(`RequestAction: GET ${url}`, normalizedData)
                response = (await ajax.get(url, config)) as TResponse
            } else {
                consoleLog(`RequestAction: POST ${url}`, normalizedData)
                let requestData: any = normalizedData

                if (normalizedData instanceof File) {
                    const formData = new FormData()
                    formData.append('file', normalizedData)
                    requestData = formData
                } else if (normalizedData instanceof FormData) {
                    requestData = normalizedData
                } else if (isRequestActionDataWithFile(normalizedData)) {
                    const formData = new FormData()
                    formData.append('file', normalizedData.file)

                    Object.entries(normalizedData).forEach(([key, value]) => {
                        if (key === 'file' || typeof value === 'undefined') {
                            return
                        }

                        if (key === 'columns' && Array.isArray(value)) {
                            value.forEach((columnItem, index) => {
                                // index
                                formData.append(
                                    `columns[${index}][index]`,
                                    String(columnItem.index)
                                )

                                // mapping object
                                if (
                                    columnItem.mapping &&
                                    typeof columnItem.mapping === 'object'
                                ) {
                                    Object.entries(columnItem.mapping).forEach(
                                        ([mapKey, mapValue]) => {
                                            formData.append(
                                                `columns[${index}][mapping][${mapKey}]`,
                                                String(mapValue)
                                            )
                                        }
                                    )
                                }
                            })
                            return
                        }

                        if (typeof value === 'string') {
                            formData.append(key, value)
                            return
                        }

                        formData.append(key, JSON.stringify(value))
                    })

                    requestData = formData
                }

                response = (await ajax.post(
                    url,
                    requestData,
                    config
                )) as TResponse
            }
            return response
        } catch (err) {
            consoleLog(`Request failed: ${url}`, err)
            return Promise.reject(err)
        }
    }
}
